import { CartProvider, setShop } from "@shopify/hydrogen";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

import { CartAttribution } from "../components/cart/CartAttribution";
import { CartWithLinesModal } from "../components/cart/CartWithLinesModal";
import { AddToBagOrBuyNowModal } from "../components/product/AddToBagOrBuyNowModal";
import type { SectionWithCheckoutProducts } from "../hooks/use-items-with-checkout";
import { useItemsWithCheckout } from "../hooks/use-items-with-checkout";
import { useMode } from "../hooks/use-mode";
import { useThemeClass } from "../hooks/use-theme-class";
import type { CheckoutEnabledConfig, LookbookProps } from "../lookbook";
import CartUIProvider from "../providers/CartUI";

const FeedView = dynamic<CheckoutViewChildProps>(async () =>
  import("./FeedView").then((module) => module.FeedView),
);
const StoryView = dynamic<CheckoutViewChildProps>(async () =>
  import("./StoryView").then((module) => module.StoryView),
);

export interface CheckoutViewRouterProps extends LookbookProps {
  playing: boolean;
  checkout: CheckoutEnabledConfig;
  setPlaying: (playing: boolean) => void;
}

export interface CheckoutViewChildProps extends CheckoutViewRouterProps {
  sections: SectionWithCheckoutProducts[];
}

export function CheckoutViewRouter(props: CheckoutViewRouterProps) {
  const mode = useMode();

  useThemeClass(mode, props.view);

  const [shopInitialized, setShopInitialized] = useState(false);

  useEffect(() => {
    if (!shopInitialized) {
      setShopInitialized(true);
      setShop(props.checkout.config);
    }
  }, [shopInitialized, props.checkout.config]);

  const { sections } = useItemsWithCheckout({
    checkout: props.checkout,
    sections: props.sections,
  });

  if (!mode || !shopInitialized) {
    return null;
  }

  const viewMarkup =
    props.view[mode] === "feed" ? (
      <FeedView {...props} sections={sections} />
    ) : (
      <StoryView {...props} sections={sections} />
    );

  return (
    <CartProvider>
      <CartUIProvider>
        {/* biome-ignore lint/complexity/noUselessFragments: CartUIProvider takes a single ReactChild */}
        <>
          {viewMarkup}
          <CartAttribution />
          <CartWithLinesModal checkout={props.checkout} />
          <AddToBagOrBuyNowModal
            checkout={props.checkout}
            onUnpause={() => props.setPlaying(true)}
          />
        </>
      </CartUIProvider>
    </CartProvider>
  );
}
